import type { SnapshotBehaviourId, SnapshotPayload } from "@healthos/shared";
import { type CSSProperties, useMemo } from "react";
import { BehaviourIcon } from "./behaviour-icon";
import { SNAPSHOT_BEHAVIOURS } from "./snapshot-tokens";

const VB_W = 900;
const CX = 450;
const CY = 320;
const RADIUS = 300;
const ORB_R = 64;
const NODE_R = 40;
const NODE_OUTER_R = NODE_R + 6;
// Labels sit on a ring outside the outermost vertex this week actually reached,
// so a maxed-out vertex never collides with its label and a quiet week does not
// leave its labels stranded at the frame. Capped at the ring a score of 100
// would need, which the viewBox margin is sized for.
const LABEL_GAP = 36;
const LABEL_RADIUS_MAX = RADIUS + NODE_OUTER_R + LABEL_GAP;
const VB_TOP = -60;
const VB_FULL_H = 760;
const LEVELS = [0.25, 0.5, 0.75, 1];
/*
 * The floor an ungraded vertex plots at.
 *
 * It has to clear the centre orb's outermost circle, not the solid disc: the
 * aura is ORB_R * 1.7 and the node itself is NODE_OUTER_R, so the reach must
 * exceed 108.8 + 46 = 154.8, which is a score of 51.6. Fifty-three clears it
 * and still sits between two gridlines, so the hull closes through the vertex
 * without it reading as a score its own label says it does not have.
 */
const RADAR_FLOOR_SCORE = 53;
// A polygon claims a shape across the axes it spans, and a shape needs three
// points. Below that the graded readings render as nodes with no hull.
const MIN_HULL_VERTICES = 3;

export interface SnapshotRadarProps {
  scores: SnapshotPayload["scores"];
  focusedId: SnapshotBehaviourId | null;
  /** Focused behaviour plus its computed correlates for the week (from the payload). */
  relatedIds: ReadonlySet<SnapshotBehaviourId>;
  quietIds: ReadonlySet<SnapshotBehaviourId>;
  onFocus?: (id: SnapshotBehaviourId | null) => void;
}

interface Vertex {
  id: SnapshotBehaviourId;
  label: string;
  color: string;
  iconName: (typeof SNAPSHOT_BEHAVIOURS)[number]["iconName"];
  angle: number;
  x: number;
  y: number;
  axisX: number;
  axisY: number;
  labelX: number;
  labelY: number;
  score: number;
  graded: boolean;
}

export function SnapshotRadar({
  scores,
  focusedId,
  relatedIds,
  quietIds,
  onFocus,
}: SnapshotRadarProps) {
  const points: Vertex[] = useMemo(() => {
    const gradedOf = (id: SnapshotBehaviourId) =>
      scores[id].status !== "quiet" && !quietIds.has(id);
    const reachOf = (id: SnapshotBehaviourId) =>
      (RADIUS * (gradedOf(id) ? scores[id].score : RADAR_FLOOR_SCORE)) / 100;
    const ring = Math.min(
      SNAPSHOT_BEHAVIOURS.reduce((m, b) => Math.max(m, reachOf(b.id)), 0) +
        NODE_OUTER_R +
        LABEL_GAP,
      LABEL_RADIUS_MAX,
    );
    return SNAPSHOT_BEHAVIOURS.map((b, i) => {
      const angle = ((i * 72 - 90) * Math.PI) / 180;
      const graded = gradedOf(b.id);
      const reach = reachOf(b.id);
      return {
        graded,
        id: b.id,
        label: b.label,
        color: b.color,
        iconName: b.iconName,
        angle,
        x: CX + Math.cos(angle) * reach,
        y: CY + Math.sin(angle) * reach,
        axisX: CX + Math.cos(angle) * RADIUS,
        axisY: CY + Math.sin(angle) * RADIUS,
        labelX: CX + Math.cos(angle) * ring,
        labelY: CY + Math.sin(angle) * ring,
        score: scores[b.id].score,
      };
    });
  }, [scores, quietIds]);

  const hull = points;
  const polyPath =
    hull.length >= MIN_HULL_VERTICES
      ? `${hull
          .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
          .join(" ")} Z`
      : null;

  return (
    <div className="snap-tree-wrap">
      <svg
        viewBox={`0 ${VB_TOP} ${VB_W} ${VB_FULL_H}`}
        preserveAspectRatio="xMidYMid meet"
        className="snap-tree-svg"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <radialGradient id="snap-radar-fill" cx="50%" cy="50%" r="65%">
            <stop offset="0%" stopColor="var(--color-teal-300)" stopOpacity="0.4" />
            <stop offset="100%" stopColor="var(--color-teal-200)" stopOpacity="0.18" />
          </radialGradient>
          <radialGradient id="snap-orb-fill" cx="38%" cy="32%" r="72%">
            <stop offset="0%" stopColor="var(--color-paper)" stopOpacity="1" />
            <stop offset="65%" stopColor="var(--color-paper)" stopOpacity="0.92" />
            <stop offset="100%" stopColor="var(--color-teal-100)" stopOpacity="0.85" />
          </radialGradient>
          <radialGradient id="snap-orb-aura" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--color-teal-200)" stopOpacity="0.45" />
            <stop offset="60%" stopColor="var(--color-teal-100)" stopOpacity="0.15" />
            <stop offset="100%" stopColor="var(--color-teal-100)" stopOpacity="0" />
          </radialGradient>
          <filter id="snap-radar-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>

        {LEVELS.map((level) => {
          const ringPts = SNAPSHOT_BEHAVIOURS.map((_, j) => {
            const a = ((j * 72 - 90) * Math.PI) / 180;
            return { x: CX + Math.cos(a) * RADIUS * level, y: CY + Math.sin(a) * RADIUS * level };
          });
          const d = `${ringPts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ")} Z`;
          return (
            <path
              key={`level-${level}`}
              d={d}
              fill="none"
              stroke="var(--color-mist-dark)"
              strokeOpacity={level === LEVELS[LEVELS.length - 1] ? 0.8 : 0.34}
              strokeWidth="1"
            />
          );
        })}

        {points.map((p, i) => (
          <g key={`ax-${p.id}`}>
            <path
              id={`snap-axis-${p.id}`}
              d={`M ${CX + Math.cos(p.angle) * ORB_R} ${CY + Math.sin(p.angle) * ORB_R} L ${p.axisX} ${p.axisY}`}
              fill="none"
              stroke="var(--color-mist-dark)"
              strokeOpacity="0.35"
              strokeWidth="1"
            />
            <circle r="3" fill={p.color} opacity="0.9">
              <animateMotion dur="4.6s" repeatCount="indefinite" begin={`${i * 0.7}s`}>
                <mpath href={`#snap-axis-${p.id}`} />
              </animateMotion>
              <animate
                attributeName="opacity"
                values="0;0.9;0.9;0"
                keyTimes="0;0.1;0.85;1"
                dur="4.6s"
                begin={`${i * 0.7}s`}
                repeatCount="indefinite"
              />
            </circle>
          </g>
        ))}

        {polyPath === null ? null : (
          <g className="snap-radar-shape">
            <path
              d={polyPath}
              fill="url(#snap-radar-fill)"
              stroke="var(--color-teal-600)"
              strokeWidth="2"
              strokeLinejoin="round"
              filter="url(#snap-radar-glow)"
            />
            <path
              d={polyPath}
              fill="none"
              stroke="var(--color-teal-700)"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
          </g>
        )}

        <g>
          <circle
            cx={CX}
            cy={CY}
            r={ORB_R * 1.7}
            fill="url(#snap-orb-aura)"
            className="snap-orb-aura-breath"
          />
          <circle cx={CX} cy={CY} r={ORB_R} fill="url(#snap-orb-fill)" />
          <circle
            cx={CX}
            cy={CY}
            r={ORB_R}
            fill="none"
            stroke="var(--color-teal-300)"
            strokeWidth="1.4"
            strokeOpacity="0.7"
          />
          <g transform={`translate(${CX - 22}, ${CY - 25}) scale(0.16)`}>
            <g className="snap-orb-leaf-grp">
              <path
                className="snap-orb-leaf snap-orb-leaf-1"
                fill="var(--color-teal-leaf-pale)"
                d="M138.72 0C155.925 0 172.413 3.07018 187.66 8.68457V8.68945C127.19 56.8294 67.91 115.27 8.79004 185.02H8.78809C3.2377 169.858 6.44659e-05 154.736 0 140.44C0 62.8805 60.7899 0.000147746 138.72 0Z"
              />
              <path
                className="snap-orb-leaf snap-orb-leaf-2"
                fill="var(--color-teal-leaf-mid)"
                d="M249.807 53.835C268.614 77.6955 279.83 107.762 279.83 140.44C279.83 209.363 201.805 297.562 154.759 316.37C154.759 316.367 154.76 316.364 154.76 316.36C169.539 205.883 213.817 119.164 249.807 53.835Z"
              />
              <path
                className="snap-orb-leaf snap-orb-leaf-3"
                fill="var(--color-teal-400)"
                d="M8.79016 185.02C26.9102 234.54 69.7102 284.45 105.16 307.23C143.24 205.62 190.76 104.78 238.84 41.4899C224.51 27.1299 207.1 15.8399 187.66 8.68994C127.19 56.8299 67.9102 115.27 8.79016 185.02Z"
              />
              <path
                className="snap-orb-leaf snap-orb-leaf-4"
                fill="var(--color-teal-600)"
                d="M138.72 320C143.43 320 148.85 318.72 154.76 316.36C169.54 205.88 213.82 119.16 249.81 53.83C246.4 49.5 242.74 45.38 238.85 41.48C190.77 104.77 143.25 205.61 105.17 307.22C117.77 315.32 129.44 319.99 138.73 319.99L138.72 320Z"
              />
            </g>
          </g>
        </g>

        {points.map((p) => {
          const dim = focusedId ? !relatedIds.has(p.id) : false;
          const focused = focusedId === p.id;
          const related = relatedIds.has(p.id) && !focused && Boolean(focusedId);
          const quietResting = !focusedId && quietIds.has(p.id);
          return (
            <g
              key={`pt-${p.id}`}
              className={`snap-radar-vertex${dim ? " is-dimmed" : ""}${focused ? " is-focused" : ""}${related ? " is-related" : ""}${quietResting ? " is-quiet-resting" : ""}${p.graded ? "" : " is-ungraded"}`}
              style={{ ["--accent" as string]: p.color, cursor: "pointer" }}
            >
              <circle cx={p.x} cy={p.y} r={NODE_R + 8} fill="transparent" />
              <circle cx={p.x} cy={p.y} r={NODE_R + 6} fill={p.color} opacity="0.10" />
              <circle
                cx={p.x}
                cy={p.y}
                r={NODE_R}
                fill="var(--color-paper)"
                stroke={p.color}
                strokeWidth="2.4"
              />
              <circle
                cx={p.x}
                cy={p.y}
                r={NODE_R - 5}
                fill="none"
                stroke={p.color}
                strokeOpacity="0.25"
                strokeWidth="1"
              />
              <foreignObject
                x={p.x - NODE_R}
                y={p.y - NODE_R}
                width={NODE_R * 2}
                height={NODE_R * 2}
              >
                <div className="snap-radar-icon" style={{ color: p.color }} aria-hidden="true">
                  <BehaviourIcon name={p.iconName} size={20} weight="duotone" />
                </div>
              </foreignObject>
            </g>
          );
        })}
      </svg>
      <div className="snap-radar-labels" aria-hidden="true">
        {points.map((p) => (
          <span
            key={`lab-${p.id}`}
            className={`snap-radar-label${p.graded ? "" : " is-quiet"}`}
            style={
              {
                left: `${((p.labelX - 0) / VB_W) * 100}%`,
                top: `${((p.labelY - VB_TOP) / VB_FULL_H) * 100}%`,
                "--accent": p.color,
                "--dx": Math.cos(p.angle).toFixed(3),
                "--dy": Math.sin(p.angle).toFixed(3),
              } as CSSProperties
            }
          >
            <b className="snap-radar-label-name">{p.label}</b>
            <em className={`snap-radar-label-score${p.graded ? "" : " is-ungraded"}`}>
              {p.graded ? p.score : "Not scored"}
            </em>
          </span>
        ))}
      </div>
      <fieldset className="snap-radar-hit-layer">
        <legend>Snapshot behavior graph</legend>
        {points.map((p) => (
          <button
            key={`hit-${p.id}`}
            type="button"
            className="snap-radar-hit"
            style={
              {
                "--accent": p.color,
                "--x": `${(p.x / VB_W) * 100}%`,
                "--y": `${((p.y - VB_TOP) / VB_FULL_H) * 100}%`,
              } as CSSProperties
            }
            aria-label={`Show ${p.label} details`}
            aria-pressed={focusedId === p.id}
            onClick={() => onFocus?.(p.id)}
          />
        ))}
      </fieldset>
    </div>
  );
}
